import React from 'react';
interface SkeletonProps {
  /** Width class (Tailwind) — default: 'w-full' */
  className?: string;
  /** Variant: 'text' (single line), 'circle', 'rect' (card-sized block) */
  variant?: 'text' | 'circle' | 'rect';
  /** Number of text lines to render (only for variant='text') */
  lines?: number;
}
/**
 * Skeleton loading placeholder — shows animated pulse blocks.
 * Use inside portal pages while data is loading.
 */
export function Skeleton({
  className = '',
  variant = 'text',
  lines = 1
}: SkeletonProps) {
  if (variant === 'circle') {
    return <div className={`rounded-full bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-1900" />;
  }
  if (variant === 'rect') {
    return <div className={`rounded-2xl bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-1901" />;
  }
  // Text variant — render multiple lines
  return <div className={`space-y-2.5 ${className}`} aria-hidden="true" data-id="element-1902">
      {Array.from({
      length: lines
    }).map((_, i) => <div key={i} className={`h-4 bg-slate-200 rounded-md animate-pulse ${i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}`} data-id="element-1903" />)}
    </div>;
}
/**
 * Skeleton card — mimics a StatCard or summary card while loading.
 */
export function SkeletonCard({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`bg-white border border-slate-200 rounded-2xl p-6 shadow-sm ${className}`} aria-hidden="true" data-id="element-1904">
      <div className="flex items-center gap-4" data-id="element-1905">
        <Skeleton variant="circle" className="w-12 h-12" data-id="element-1906" />
        <div className="flex-1 space-y-2" data-id="element-1907">
          <Skeleton className="w-24" data-id="element-1908" />
          <Skeleton className="w-16 h-6" data-id="element-1909" />
        </div>
      </div>
    </div>;
}
/**
 * Skeleton page — full page loading state for portal pages.
 * Shows 3 summary cards + a content block.
 */
export function SkeletonPage() {
  return <div className="max-w-7xl mx-auto p-6 space-y-8" aria-busy="true" aria-label="Laden..." data-id="element-1910">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1911">
        <SkeletonCard data-id="element-1912" />
        <SkeletonCard data-id="element-1913" />
        <SkeletonCard data-id="element-1914" />
      </div>

      {/* Content block */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-id="element-1915">
        <Skeleton className="w-48 h-5" data-id="element-1916" />
        <div className="space-y-3" data-id="element-1917">
          {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="flex items-center gap-4" data-id="element-1918">
              <Skeleton variant="circle" className="w-10 h-10" data-id="element-1919" />
              <div className="flex-1" data-id="element-1920">
                <Skeleton className="w-32" data-id="element-1921" />
              </div>
              <Skeleton variant="rect" className="w-24 h-3" data-id="element-1922" />
              <Skeleton className="w-10 h-5" data-id="element-1923" />
            </div>)}
        </div>
      </div>
    </div>;
}